import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Button, Grid, Icon, Image } from 'semantic-ui-react';
import agent from '../../../app/api/agent';
import VerifyDragDrop from '../../Dashboard/Receiving/VerifyImageUpload/VerifyDragDrop';
import { v4 as uuid } from 'uuid';
import { userLanguage } from '../../../app/common/userLanguageSchema/userLanguage';
import { useStore } from '../../../app/stores/store';
import { FileUploadInterface } from '../../../app/models/Profile';
import ImageTag from '../../Profile/RenderImage';
import { updateHistoryMain } from '../../../app/models/history';
import { toast } from 'react-toastify';
import UploadProgressIndicator from '../../../app/common/components/UploadProgressIndicator';
import { normalizeDeliverablesQcRows } from './deliverablesQcUtils';

export type FileType = {
  id: string;
  fileId: string;
  fileName: string;
  url: string;
};

interface Props {
  elementName: string;
  filesUploaded: any;
  getUploadedFiles: (uploadedFiles: FileType, index: number) => void;
  index: number;
  isMandatory: boolean;
  errorMessage: string;
  fileUploadIndex?: number;
  totalFileUploads?: number;
  allElementsData?: any[];
}
const FileUpload = ({
  elementName,
  filesUploaded,
  getUploadedFiles,
  isMandatory,
  errorMessage,
  fileUploadIndex = 0,
  totalFileUploads = 1,
  allElementsData = [],
}: Props) => {
  const {
    commonStore,
    activityStore: { selectedActivity, downloadFilesStore },
    dashboardStore,
    WorkLevelStore,
    reportStore,
    userStore:{ user },
    CRMStore,
    ElementStore,
    metaDataStore,
    viewStore,
    masteringStore
  } = useStore();
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<FileType>({
    id: '',
    fileId: '',
    fileName: '',
    url: '',
  });

  // Calculate completed file uploads dynamically
  const getCompletedUploadCount = (): number => {
    if (allElementsData.length === 0 || totalFileUploads <= 1) return 0;
    
    let completedCount = 0;
    allElementsData.forEach((el: any) => {
      if (el.elementType === 'AddFiles' && el.files && el.files.length > 0) {
        completedCount++;
      }
    });
    return completedCount;
  };

  const completedFiles = getCompletedUploadCount();
  const shouldShowIndicator = totalFileUploads > 1 && fileUploadIndex === 0;

  const onDrop = (acceptedFiles: File[]) => {
    setFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      ),
    );
  };

const fileUploadHandler = async (url: string) => {
  const { workflowSubTask } = ElementStore;
  const { subTaskName, subTaskId, elements = [] } = workflowSubTask;

  metaDataStore.masteringFileUpload = true;

  const isQCSubtask = ['DPX QC Report', 'DSM QC Report'].includes(subTaskName);
  const isDeliverables = subTaskName === 'Deliverables QC Report';
  const isDcpSubtask = ['Encrypted QC Report', 'Open QC Report'].includes(subTaskName);

  /* ---------------------------------------------
     1️⃣ DPX / DSM QC REPORT
  ----------------------------------------------*/
  if (isQCSubtask) {
    if (elementName === 'Check Sum Upload') {
      // const element = elements.find(e => e.elementName === 'Check Sum Upload');

      const suffix =
        subTaskName === 'DPX QC Report'
          ? '_Restored_RAWcooked.mkv'
          : '_Restored_DSM_RAWcooked.mkv';

      const { data } = await agent.NFAIReport.CheckSumReader(
        url,
        subTaskId,
        suffix
      );

      return data;
    }

    const { data } = await agent.NFAIReport.MetadataUploadMastring(
      subTaskId,
      url
    );

    if (!data?.length || data.includes('Please upload DPX file')) {
      toast.error('Kindly upload correct file...');
    }

    return data;
  }

  /* ---------------------------------------------
     2️⃣ DELIVERABLES QC REPORT
  ----------------------------------------------*/
  if (isDeliverables) {
    const qcReport = elements.find(x => x.elementName === 'QC REPORT');
    const subtitles = elements.find(x => x.elementName === 'subtitles available');

    let elementValue: any[] = [];

    if (!qcReport?.elementValue || qcReport.elementValue === 'DynamicTable|[]') {
      const res = await metaDataStore.DeliverablesParameters(
        selectedActivity?.id || '',
        subTaskId
      );

      elementValue = normalizeDeliverablesQcRows(
        res.map((x: any) => ({
          ...x,
          QC_File_Format: x.qC_File_Format ?? "",
          Total_File_Size: x.Total_File_Size ?? "",
          File_Creation_Date: x.File_Creation_Date ?? "",
          SRT_File_Available: x.SRT_File_Available ?? "",
          SRT_Sync: x.SRT_Sync ?? "",
          Watermark: x.Watermark ?? ""
        }))
      );
    } else {
      elementValue = normalizeDeliverablesQcRows(
        JSON.parse(qcReport.elementValue.split('|')[1] || '[]')
      );
    }

    const uploadMap: Record<string, number> = {
      'ProRes UHD File Upload': 0,
      'ProRes FHD File Upload': 1,
      'MP4 File Upload': 2,
      'MP4 watermark File Upload': 3,
    };

    const checksumMap: Record<string, number> = {
      'ProRes UHD Check Sum Upload': 0,
      'ProRes FHD Check Sum Upload': 1,
      'MP4 Check Sum Upload': 2,
      'MP4 watermark Check Sum Upload': 3,
    };

    if (uploadMap[elementName] !== undefined) {
      const { data } = await agent.NFAIReport.deliverables(
        uploadMap[elementName],
        subtitles?.elementValue || 'No',
        subTaskId,
        url,
        elementValue
      );
      return data;
    }

    if (checksumMap[elementName] !== undefined) {
      const { data } = await agent.NFAIReport.DeliverableCheckSumReader(
        checksumMap[elementName],
        subTaskId,
        url,
        elementValue
      );
      return data;
    }
  }

  /* ---------------------------------------------
     3️⃣ DCP QC REPORT (Encrypted / Open)
  ----------------------------------------------*/
  if (isDcpSubtask) {
    const subtitles = elements.find(x => x.elementName === 'SUBTITLE LANGUAGE');

    const subtaskElementName =
      subTaskName === 'Encrypted QC Report'
        ? 'DCP (Encrypted) QC Report'
        : 'DCP (Open) QC Report';

    const storedValue =
      viewStore.metadata.find(item => item.elementName === subtaskElementName)
        ?.elementValue || '';

    let elementValue: any[] = [];

    if (!storedValue || storedValue === 'DynamicTable|[]') {
      const res =
        subTaskName === 'Encrypted QC Report'
          ? await agent.NFAI.checkparametersEncrypted(
              selectedActivity?.title.split('-')[1]?.trim().split(' ')[0] || "",
              selectedActivity?.id || ''
            )
          : await agent.NFAI.checkparametersOpen(selectedActivity?.id || '');

      elementValue =
        res?.data?.map((item: any) => ({
          "CHECK PARAMETERS": item["check_Parameters"],
          "TECHNICAL DISCRIPTION": item["technical_Description"],
          "WARNING / PASS / FAIL": item["warning_Pass_Fail"],
          "REMARKS": item["remarks"],
        })) || [];
    } else {
      elementValue = JSON.parse(storedValue.split('|')[1] || '[]');
    }

    const { data } = await agent.NFAIReport.dcpEncrypted(
      url,
      selectedActivity?.id || '',
      !subtitles?.elementValue || subtitles.elementValue === "NA" ? "No" : "Yes",
      subtaskElementName,
      elementValue
    );

    return data;
  }
};


  useEffect(() => {
    if (commonStore.devEnv)
      WorkLevelStore.loadWorkLevelBasedOnItemLevel(selectedActivity?.id);
    getUploadedFiles(uploadedFiles, filesUploaded.elementId);
    // eslint-disable-next-line
  }, [uploadedFiles, selectedActivity]);

  const upload = (e: any) => {
    e.preventDefault();
    files.forEach(async (file) => {
      let FileNameChangedata: FileUploadInterface = {
        fileId: '',
        id: '',
        fileName: '',
        url: '',
      };
      if (commonStore.devEnv) {
        const item = selectedActivity?.id || filesUploaded.elementId;
        const workLevel = dashboardStore.dashboard.dashboardWorkLevelMaster?.id;
        const response = await agent.Files.localFileUpload(
          file,
          workLevel,
          item,
          ['Ungraded RAWcooked File Upload',
            'Check Sum Upload',
            'DSM Seamless RAWcooked File Upload',
            'ProRes UHD File Upload',
            'ProRes FHD File Upload',
            'MP4 File Upload',
            'MP4 watermark File Upload',
            'DCP Encrypted File Upload',
            'DCP Open File Upload'
          ].includes(elementName) ? 'Mastering Files' : undefined
        );
        const url = response?.data.path.replace(/\\\\/g, '\\');
        const filename = url.substring(url.lastIndexOf('\\') + 1);
        if(commonStore.NFAIReportCustomer){
          if(ElementStore.workflowSubTask.subTaskName === 'Restoration' && elementName === 'Restoration File Upload'){
            metaDataStore.masteringFileUpload = true;
            const elements =  (data:string)  => ElementStore.workflowSubTask.elements?.find((el) => el.elementName === data)?.elementId || "";
            const { data } = await agent.NFAIReport.Restoration_File_upload(url || "", elements('Image Restoration') , elements('NFHM QC Restoration Supervisor Feedback'), selectedActivity?.id || "");
            if(typeof data === 'string' && data?.includes('Expected Restoration Report sheet not found.')) {
              toast.error(`${data}`);
            }
          }
          const subTaskNameExceptions = ['encrypted qc report', 'open qc report', 'deliverables qc report', 'dpx qc report', 'dsm qc report', 'lto qc report'];
          const subtaskNames = subTaskNameExceptions.includes(ElementStore.workflowSubTask?.subTaskName?.toLowerCase());
          if(subtaskNames) {
            const Toast = await fileUploadHandler(url);
            if(Array.isArray(Toast) && Toast.length > 0){
              masteringStore.deliverableError = Toast;
              const toastMessage = Toast
              .map(item => `${item.mediaType} - ${item.key}: ${item.value}`)
              toastMessage.map((message: string) => toast.error(message));
            return;
            }
            else if(!(Toast as any || "").includes('DynamicTable|')) {
                toast.error(Toast);
                return;
              }
          }
          let lastIndex = url.lastIndexOf('\\');
         let result = url.substring(0, lastIndex);
         if(ElementStore.workflowSubTask?.subTaskName?.toLowerCase().includes('reel analysis')) { 
           reportStore.HandleReelAnalysis(response.data.item,result,filename);
         }
          const payload = {
            taskId: '',
            taskName: '',
            subTaskId: '',
            subTaskName: '',
            status: `added file ${file.name} for`,
            date: new Date(),
          };
          updateHistoryMain(payload, false);
        }
        FileNameChangedata = {
          fileId: uuid(),
          id: response.data.id,
          url: url,
          fileName: filename
            .concat(' Task Form ')
            .concat(new Date().toLocaleString()),
        };
        dashboardStore.getLocalFilesBasedOnItem(item);
        if(user?.customerName === 'PCPLCRM') {
          CRMStore.fileUploadDetails.push(filesUploaded.elementId)
        toast.success('File upload successfully.')
        }
      } else {
        const response = await agent.Files.uploadFile(file);
        const subTaskNameExceptions = ['encrypted qc report', 'open qc report', 'deliverables qc report', 'dpx qc report', 'dsm qc report', 'lto qc report'];
          const subtaskNames = subTaskNameExceptions.includes(ElementStore.workflowSubTask?.subTaskName?.toLowerCase());
          if(subtaskNames) {
        const Toast = await fileUploadHandler(response.data.url || "");
       if(!(Toast as any).includes('DynamicTable|')) {
              toast.error(Toast);
              return;
            }
          }
        FileNameChangedata = {
          fileId: uuid(),
          id: response.data.id,
          url: response.data.url,
          fileName: response.data.fileName
            .concat('Task Form ')
            .concat(new Date().toLocaleString()),
        };
      }
      setUploadedFiles(FileNameChangedata);
    });
    setFiles([]);
  };

  const downloadFile = async (url: string, fileName: string) => {
    if (commonStore.devEnv) {
      downloadLocalFile(url, fileName);
    } else {
      const fileExec: any = url?.split('.')?.pop();
      await downloadFilesStore(url, fileName + '.' + fileExec);
    }
  };

  const downloadLocalFile = (url: string, fileName: string) => {
    const splitPath: string[] = url.split('build') || [];
    const link = document.createElement('a');
    link.href = splitPath[1];
    link.setAttribute('download', fileName);
    link.setAttribute('target', 'blank');

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode?.removeChild(link);
  };

  return (
    <div>
      {/* Multi-Upload Progress Indicator - Outside Grid for proper sticky positioning */}
      {shouldShowIndicator && (
        <UploadProgressIndicator
          completedUploads={completedFiles}
          totalUploads={totalFileUploads}
        />
      )}

      <div style={user?.customerName === 'PCPLCRM' ? {width:'50%'} : {}}>
      <Grid.Row>
        <Grid.Column>
          <label
            style={{
              color: 'black',
              marginBottom: '12px',
              display: 'block',
            }}
          >
            <strong>{elementName}</strong>
            {isMandatory ? <span style={{ color: '#9f3a38' }}>*</span> : null}
          </label>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        {filesUploaded?.files &&
          filesUploaded?.files?.map((f: FileType) => {
            return (
              <>
                <Grid.Column
                  style={{ marginBottom: '10px' }}
                  width={3}
                  textAlign={'center'}
                >
                  {f.url.match(/\.(gif|jpe?g|tiff?|png|webp|bmp|svg)$/i) ? (
                    <ImageTag
                      imageUrl={f?.url}
                      defaultUrl="/assets/documents.jpg"
                      style={{
                        display: 'inline-block',
                        marginRight: '20px',
                        marginBottom: '10px',
                        height: '70px',
                      }}
                    />
                  ) : (
                    <Image
                      src="/assets/documents.jpg"
                      alt="file upload"
                      style={{
                        display: 'inline-block',
                        marginRight: '20px',
                        marginBottom: '10px',
                        height: '70px',
                      }}
                    />
                  )}

                  <Icon
                    name="download"
                    onClick={() => downloadFile(f.url, f.fileName)}
                  />
                  <p
                    style={{
                      color: 'black',
                      alignItems: 'center',
                      // width: "100px",
                      // overflow: "hidden",
                      // textOverflow: "ellipsis",
                      // whiteSpace: "nowrap",
                      display: 'inline-block',
                    }}
                  >
                    {f.fileName}
                  </p>
                </Grid.Column>
              </>
            );
          })}
      </Grid.Row>
      <VerifyDragDrop
        files={files}
        onDrop={onDrop}
        error={isMandatory && errorMessage !== ''}
        elementName={elementName}
      />
      {errorMessage && (
        <Grid.Row>
          <Grid.Column>
            <span className="errorStyles">{errorMessage}</span>
          </Grid.Column>
        </Grid.Row>
      )}
      <Button
        onClick={(e) => upload(e)}
        positive
        //Upload
      >
        {
          (userLanguage[0].Level3InventoryRecievingVerify?.IRVB04 as any)?.[
            `${commonStore.language.key}`
          ]
        }
      </Button>

      {/* Divider line between multiple file uploads */}
      {totalFileUploads > 1 && fileUploadIndex < totalFileUploads && (
        <div
          style={{
            marginTop: '20px',
            marginBottom: '20px',
            borderBottom: '2px solid #000',
            paddingBottom: '20px',
          }}
        />
      )}
      {/* {uploadedFiles?.length > 0 && (
        <Segment>
          <Grid>
            {uploadedFiles?.map((url) => {
              return (
                <Grid.Column textAlign={"center"} key={url.url} width={2}>
                  <Image
                    src={
                      url.url.includes("pdf")
                        ? "/assets/documents.jpg"
                        : url.url
                    }
                    alt="/assets/documents.jpg"
                  />
                </Grid.Column>
              );
            })}
          </Grid>
        </Segment>
      )} */}
      </div>
    </div>
  );
};

export default observer(FileUpload);
